import cx from "classnames";
import React, { ReactNode, useCallback, useMemo, useState } from "react";

import { Models } from "api/types";
import { Icon } from "atoms/icon/Icon";
import DownChevron from "atoms/icon/icons/chevron_down_small.svg";
import FilterItems from "atoms/icon/icons/filter_items.svg";
import { ModalFilterInterface } from "organisms/selection-modals/modal-filter-interface/ModalFilterInterface";
import { ModalSelectorFilterList } from "organisms/selection-modals/modal-selector-filter-list/ModalSelectorFilterList";
import { useRetailProductFilterAndSort } from "pages/root/shop/useRetailProductFilterAndSort";

import { ListingSortOptions } from "./FilterAndSortListings";

const DEFAULT_SORT = "Featured";
const MODAL_TYPES = {
  FILTERS: "filters" as const,
  SORT: "sort" as const,
} as const;

type ModalType = (typeof MODAL_TYPES)[keyof typeof MODAL_TYPES];

const useProductSearch = (
  products: Models<"DisplayRetailProductListing">[],
  searchQuery: string,
  derivedActiveView: string
) => {
  return useMemo(() => {
    let filtered = products;

    if (derivedActiveView === "featured") {
      filtered = filtered.filter((product) => product.isFeaturedProduct);
    }

    if (derivedActiveView === "best-sellers") {
      filtered = filtered.filter((product) => product.isBestSeller);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (product) =>
          product.name.text.toLowerCase().includes(query) ||
          product.description.text.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [products, searchQuery, derivedActiveView]);
};

const createModalProps = (
  modalType: ModalType,
  activeModal: ModalType | null,
  onClose: () => void
) => ({
  open: activeModal === modalType,
  onClose,
});

const createArrayChangeHandler =
  (
    filterType:
      | "concerns"
      | "areas"
      | "brands"
      | "productTypes"
      | "fulfillmentOptions",
    filterAndSortState: any
  ) =>
  (items: string[]) => {
    const currentPending = filterAndSortState.pendingFilters;
    const toRemove = currentPending[filterType].filter(
      (item: string) => !items.includes(item)
    );
    const toAdd = items.filter(
      (item: string) => !currentPending[filterType].includes(item)
    );

    const setters = {
      concerns: filterAndSortState.setConcerns,
      areas: filterAndSortState.setAreas,
      brands: filterAndSortState.setBrands,
      productTypes: filterAndSortState.setProductTypes,
      fulfillmentOptions: filterAndSortState.setFulfillmentOptions,
    };

    for (const item of [...toRemove, ...toAdd]) {
      setters[filterType](item);
    }
  };

const EmptyProductsState: React.FC<{ onClearFilters: () => void }> = ({
  onClearFilters,
}) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="mb-2 whitespace-nowrap text-body1 text-primary">
      No products available with current filters
    </div>
    <div className="mb-2 whitespace-nowrap text-body2 text-secondary">
      Try adjusting your filters or search terms
    </div>
    <button
      onClick={onClearFilters}
      className="bg-brand hover:bg-brand-dark rounded-lg px-4 text-bold3 underline"
    >
      Clear All Filters
    </button>
  </div>
);

interface RetailProductProps {
  retailProducts: (Models<"DisplayRetailProductListing"> & {
    discountText?: string;
    customColor?: string | null;
  })[];
  concerns: Models<"ConcernModel">[];
  areas: Models<"AreaResult">[];
  brands?: Array<{ id: string; name: Models<"Translation"> }>;
  productTypes?: Array<{ id: string; name: Models<"Translation"> }>;
  children: (
    product: Models<"DisplayRetailProductListing"> & {
      discountText?: string;
      customColor?: string | null;
    },
    index: number
  ) => ReactNode;
  onFilteredCountChange?: (count: number) => void;
}

export interface PlatformLocationProductAvailability {
  platformLocationId: string;
  isInStock: boolean;
}

export const FilterAndSortRetailProducts: React.FC<RetailProductProps> = ({
  retailProducts,
  concerns,
  areas,
  brands = [],
  productTypes = [],
  children: renderItem,
  onFilteredCountChange,
}) => {
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);
  const [searchQuery] = useState("");
  const [activeView, setActiveView] = useState<
    "all" | "featured" | "best-sellers" | "filtered"
  >("all");

  const filterAndSortState = useRetailProductFilterAndSort(retailProducts);

  const hasActiveFilters = filterAndSortState.hasActiveFilters;
  const derivedActiveView =
    hasActiveFilters && activeView === "all" ? "filtered" : activeView;

  const handleModalClose = useCallback(() => {
    setActiveModal(null);
    filterAndSortState.restore();
  }, [filterAndSortState]);

  const handleFiltersClick = useCallback(() => {
    setActiveModal(MODAL_TYPES.FILTERS);
    filterAndSortState.saveCheckpoints();
  }, [filterAndSortState]);

  const handleSortClick = useCallback(() => {
    setActiveModal(MODAL_TYPES.SORT);
    filterAndSortState.saveCheckpoints();
  }, [filterAndSortState]);

  const handleViewAll = useCallback(() => {
    filterAndSortState.clearConcerns();
    filterAndSortState.clearAreas();
    filterAndSortState.clearBrands();
    filterAndSortState.clearProductTypes();
    filterAndSortState.clearFulfillmentOptions();
    setActiveView("all");
  }, [filterAndSortState]);

  const handleBestSellers = useCallback(() => {
    setActiveView("best-sellers");
  }, []);

  const handleClearFilters = useCallback(() => {
    filterAndSortState.clearFilters();
    setActiveView("all");
  }, [filterAndSortState]);

  const { availableBrands, availableProductTypes } = useMemo(() => {
    const safeBrands = Array.isArray(brands) ? brands : [];
    const safeProductTypes = Array.isArray(productTypes) ? productTypes : [];

    const availableBrandIds = new Set(
      retailProducts.map((product) => product.brandId)
    );
    const availableProductTypeIds = new Set(
      retailProducts.flatMap((product) => product.productTypeIds || [])
    );

    return {
      availableBrands: safeBrands.filter((brand) =>
        availableBrandIds.has(brand.id)
      ),
      availableProductTypes: safeProductTypes.filter((productType) =>
        availableProductTypeIds.has(productType.id)
      ),
    };
  }, [retailProducts, brands, productTypes]);

  const hasBestSellers = useMemo(() => {
    return retailProducts.some((product) => product.isBestSeller);
  }, [retailProducts]);

  const searchFilteredResults = useProductSearch(
    filterAndSortState.filteredListings,
    searchQuery,
    derivedActiveView
  );

  onFilteredCountChange?.(searchFilteredResults.length);

  const modalContent = useMemo(() => {
    if (activeModal === MODAL_TYPES.FILTERS) {
      return (
        <ModalFilterInterface
          {...createModalProps(
            MODAL_TYPES.FILTERS,
            activeModal,
            handleModalClose
          )}
          concerns={concerns}
          areas={areas}
          brands={availableBrands}
          productTypes={availableProductTypes}
          fulfillmentOptions={filterAndSortState.availableFulfillmentOptions}
          hasSingleFulfillmentOption={
            filterAndSortState.hasSingleFulfillmentOption
          }
          selectedConcerns={filterAndSortState.pendingFilters.concerns}
          selectedAreas={filterAndSortState.pendingFilters.areas}
          selectedBrands={filterAndSortState.pendingFilters.brands}
          selectedProductTypes={filterAndSortState.pendingFilters.productTypes}
          selectedInStoreAndDelivery={
            filterAndSortState.pendingFilters.fulfillmentOptions
          }
          selectedSort={filterAndSortState.pendingFilters.sort}
          sortOptions={[...filterAndSortState.sortOptions].map(
            ([id, title]) => ({ id, title })
          )}
          onConcernsChange={createArrayChangeHandler(
            "concerns",
            filterAndSortState
          )}
          onAreasChange={createArrayChangeHandler("areas", filterAndSortState)}
          onBrandsChange={createArrayChangeHandler(
            "brands",
            filterAndSortState
          )}
          onProductTypesChange={createArrayChangeHandler(
            "productTypes",
            filterAndSortState
          )}
          onDeliveryChange={createArrayChangeHandler(
            "fulfillmentOptions",
            filterAndSortState
          )}
          onSortChange={(sort) =>
            filterAndSortState.sortBy(sort as ListingSortOptions)
          }
          onApply={() => {
            filterAndSortState.applyFilters();
            setActiveModal(null);
          }}
          onClearAll={() => {
            setActiveModal(null);
            filterAndSortState.clearFilters();
            setActiveView("all");
          }}
        />
      );
    }

    if (activeModal === MODAL_TYPES.SORT) {
      return (
        <ModalSelectorFilterList
          {...createModalProps(MODAL_TYPES.SORT, activeModal, handleModalClose)}
          title="SORT BY"
          type="sort"
          items={[...filterAndSortState.sortOptions].map(([id, title]) => ({
            id,
            title,
          }))}
          checkedItems={[filterAndSortState.pendingFilters.sort]}
          onChange={(id: string) => {
            filterAndSortState.sortBy(id as ListingSortOptions);
          }}
          firstButtonLabel="Apply"
          onFirstButtonClick={() => {
            filterAndSortState.applyFilters();
            setActiveModal(null);
            filterAndSortState.setSearchParams();
          }}
        />
      );
    }

    return null;
  }, [
    activeModal,
    concerns,
    areas,
    availableBrands,
    availableProductTypes,
    filterAndSortState,
    filterAndSortState.pendingFilters,
    handleModalClose,
  ]);

  return (
    <div className="flex flex-col bg-two px-4 pt-2">
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        <button
          className={cx(
            "flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-full",
            activeModal === MODAL_TYPES.FILTERS || hasActiveFilters
              ? "bg-brand-tint"
              : "bg-white"
          )}
          onClick={handleFiltersClick}
        >
          <Icon svg={FilterItems} size="small" color="brandColor" />
        </button>

        <button
          className={cx(
            "flex-1 whitespace-nowrap rounded-full px-4 py-2 text-bold3 text-black",
            derivedActiveView === "all" && !hasActiveFilters
              ? "bg-brand-tint"
              : "bg-white"
          )}
          onClick={handleViewAll}
        >
          View All
        </button>

        {hasBestSellers && (
          <button
            className={cx(
              "flex-1 whitespace-nowrap rounded-full px-4 py-2 text-bold3 text-black",
              derivedActiveView === "best-sellers"
                ? "bg-brand-tint"
                : "bg-white"
            )}
            onClick={handleBestSellers}
          >
            Best Sellers
          </button>
        )}

        <button
          className="flex flex-1 items-center rounded-full bg-white px-3 py-1.5 text-bold3 text-black"
          onClick={handleSortClick}
        >
          <span className="overflow-hidden text-ellipsis whitespace-nowrap text-left">
            Sort:{" "}
            {filterAndSortState.sortOptions.get(
              filterAndSortState.pendingFilters.sort
            ) || DEFAULT_SORT}
          </span>
          <Icon svg={DownChevron} size="xsmall" color="primaryText" />
        </button>
      </div>

      {modalContent}

      {searchFilteredResults.length === 0 ? (
        <EmptyProductsState onClearFilters={handleClearFilters} />
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {searchFilteredResults.map((item, index) => (
            <React.Fragment key={item.id}>
              {renderItem(item, index)}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};
